import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Toaster } from './ui/sonner';
import { Users, Package, LogOut, Menu, X, Shield } from 'lucide-react';

const navItems = [
  { label: 'Users', path: '/superbackend', icon: Users, end: true },
  { label: 'Packages', path: '/superbackend/packages', icon: Package, end: false },
];

export default function SuperAdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const superUser = JSON.parse(localStorage.getItem('sellmu_super_user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('sellmu_super_token');
    localStorage.removeItem('sellmu_super_user');
    navigate('/superbackend/login');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'bg-[#EB7B44] text-white shadow-sm'
        : 'text-white/60 hover:bg-white/10 hover:text-white'
    }`;

  const sidebarContent = (
    <div className="flex flex-col h-full" data-testid="super-sidebar">
      <div className="px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#EB7B44] flex items-center justify-center">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <span className="text-lg font-bold text-white block leading-tight" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
            <span className="text-[10px] text-white/40 uppercase tracking-widest font-medium">Super Admin</span>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={linkClass}
            onClick={() => setMobileOpen(false)}
            data-testid={`super-nav-${item.label.toLowerCase()}`}
          >
            <item.icon size={18} strokeWidth={1.5} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#EB7B44]/20 flex items-center justify-center text-[#EB7B44] font-semibold text-sm">
            {superUser?.username?.charAt(0)?.toUpperCase() || 'S'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{superUser?.username || 'Super Admin'}</p>
            <p className="text-xs text-white/40 truncate">Super Admin</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-white/50 hover:text-red-400 hover:bg-red-500/10"
          onClick={handleLogout}
          data-testid="super-logout-btn"
        >
          <LogOut size={16} className="mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F5F5]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] bg-[#0f172a] h-screen fixed left-0 top-0 z-30">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[280px] bg-[#0f172a] shadow-2xl animate-slide-in">
            <div className="flex justify-end p-2">
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-white/10">
                <X size={20} className="text-white/60" />
              </button>
            </div>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="lg:ml-[260px]">
        <header className="lg:hidden glass-header sticky top-0 z-20 px-4 py-3 border-b border-[#DFDEDE] flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="p-2 rounded-lg hover:bg-[#F6F5F5]" data-testid="super-mobile-menu-btn">
            <Menu size={22} className="text-[#64584F]" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#EB7B44] flex items-center justify-center">
              <Shield size={14} className="text-white" />
            </div>
            <span className="font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Super Admin</span>
          </div>
        </header>
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
